import { Injectable, signal, computed } from '@angular/core';
import Keycloak from 'keycloak-js';
import { environment } from '../../../environments/environment';
import { UserProfile } from '../models/user.model';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private keycloak: Keycloak;
  private initialized = signal(false);
  private authenticated = signal(false);
  private userProfile = signal<UserProfile | null>(null);

  readonly isInitialized = computed(() => this.initialized());
  readonly isAuthenticated = computed(() => this.authenticated());
  readonly profile = computed(() => this.userProfile());

  constructor() {
    this.keycloak = new Keycloak({
      url: environment.keycloak.url,
      realm: environment.keycloak.realm,
      clientId: environment.keycloak.clientId
    });
  }

  async init(): Promise<boolean> {
    try {
      const authenticated = await this.keycloak.init({
        onLoad: 'check-sso',
        silentCheckSsoRedirectUri: window.location.origin + '/assets/silent-check-sso.html',
        checkLoginIframe: false
      });

      this.authenticated.set(authenticated);
      this.initialized.set(true);

      if (authenticated) {
        await this.loadUserProfile();
        this.setupTokenRefresh();
      }

      return authenticated;
    } catch (error) {
      console.error('Keycloak initialization failed:', error);
      this.initialized.set(true);
      return false;
    }
  }

  async login(): Promise<void> {
    await this.keycloak.login({
      redirectUri: window.location.origin + '/dashboard'
    });
  }

  async logout(): Promise<void> {
    await this.keycloak.logout({
      redirectUri: window.location.origin + '/login'
    });
  }

  async getToken(): Promise<string | undefined> {
    try {
      // Refresh token if it expires in the next 30 seconds
      await this.keycloak.updateToken(30);
      return this.keycloak.token;
    } catch (error) {
      console.error('Failed to refresh token:', error);
      await this.login();
      return undefined;
    }
  }

  getTokenSync(): string | undefined {
    return this.keycloak.token;
  }

  private async loadUserProfile(): Promise<void> {
    try {
      const profile = await this.keycloak.loadUserProfile();
      this.userProfile.set({
        sub: this.keycloak.subject || '',
        email: profile.email,
        email_verified: profile.emailVerified,
        preferred_username: profile.username,
        given_name: profile.firstName,
        family_name: profile.lastName,
        name: `${profile.firstName || ''} ${profile.lastName || ''}`.trim()
      });
    } catch (error) {
      console.error('Failed to load user profile:', error);
      // Use token claims as fallback
      if (this.keycloak.tokenParsed) {
        const parsed = this.keycloak.tokenParsed as Record<string, unknown>;
        this.userProfile.set({
          sub: this.keycloak.subject || '',
          email: parsed['email'] as string | undefined,
          preferred_username: parsed['preferred_username'] as string | undefined,
          given_name: parsed['given_name'] as string | undefined,
          family_name: parsed['family_name'] as string | undefined,
          name: parsed['name'] as string | undefined
        });
      }
    }
  }

  private setupTokenRefresh(): void {
    // Refresh token every 60 seconds
    setInterval(async () => {
      if (this.keycloak.authenticated) {
        try {
          await this.keycloak.updateToken(70);
        } catch (error) {
          console.error('Failed to refresh token:', error);
        }
      }
    }, 60000);
  }

  hasRole(role: string): boolean {
    return this.keycloak.hasRealmRole(role) || this.keycloak.hasResourceRole(role);
  }

  getRoles(): string[] {
    const realmRoles = this.keycloak.realmAccess?.roles || [];
    const resourceRoles = Object.values(this.keycloak.resourceAccess || {})
      .flatMap(access => access.roles || []);
    return [...new Set([...realmRoles, ...resourceRoles])];
  }
}
